/**
 * The file name a `Content-Disposition` header asks for, or `null` when it carries none.
 *
 * Callers keep their own fallback: this returns `null` rather than inventing a name, so
 * a server that sends no header leaves the caller's behaviour unchanged.
 *
 * RFC 5987's `filename*` wins over the plain `filename` when both are present, because
 * only the encoded form can carry non-ASCII — a server sending both is offering the
 * plain one as the degraded copy.
 */
export function filenameFromContentDisposition(header: string | null | undefined): string | null {
  if (!header) return null;

  // The charset token is matched but not honoured — the value is always decoded as UTF-8,
  // which every real sender uses and which RFC 5987 requires support for. A server
  // declaring ISO-8859-1 would be decoded wrongly rather than rejected; no sender we talk
  // to does, and guessing beats refusing a name we only wanted as a hint.
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (extended) {
    try {
      const decoded = decodeURIComponent(extended[2].trim());
      if (decoded) return decoded;
    } catch {
      // A malformed percent-escape. Fall through to the plain name rather than failing
      // the whole download over a header we only wanted as a hint.
    }
  }

  // Search for the plain parameter with any `filename*` parameter cut out first. Both
  // patterns below match on a substring, and an undecodable extended value that happens
  // to contain the text "filename=" would otherwise be mined for a name — a header of
  // `filename*=UTF-8''bad%ZZfilename=evil.zip` yielded "evil.zip". Cutting the parameter
  // rather than splitting the header on ";" keeps a quoted name containing a semicolon
  // intact, which the pattern below handles deliberately.
  const plain = header.replace(/filename\*\s*=\s*[^;]*/i, '');

  const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(plain);
  if (quoted) {
    return quoted[1].replaceAll(/\\(.)/g, '$1') || null;
  }

  const bare = /filename\s*=\s*([^;]+)/i.exec(plain);
  if (bare) {
    return bare[1].trim() || null;
  }

  return null;
}
